import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  PasswordInput,
  ScrollArea,
  TextInput,
  Stack,
  MultiSelect,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { UPDATE_STAFF } from "apollo/mutuations";
import { GET_MY_SUPPLIERS_Business } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

export const StaffUpdateModal = ({ setOpened, editRow }) => {
  const [businessDropDownData, setBusinessDropDownData] = useState([]);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",

      business_ids: [], // ← string[]
    },

    validate: {
      name: (value) => (value.trim().length < 2 ? "Name is too short" : null),
      business_ids: (value) =>
        value.length === 0 ? "Please select at least one business" : null,
    },
  });

  /* -------------------- set default values on edit -------------------- */
  useEffect(() => {
    if (!editRow) return;

    form.setValues({
      name: editRow.name || "",
      business_ids: editRow.supplierBusinesses?.map((b) => String(b.id)) || [],
    });
  }, [editRow]);

  const { loading: businessLoading } = useQuery(GET_MY_SUPPLIERS_Business, {
    variables: {
      first: 100,
      page: 1,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onCompleted: (data) => {
      const businesses =
        data?.myBusinesses?.data?.map((business) => ({
          label: business.business_name,
          value: String(business.id),
        })) || [];

      setBusinessDropDownData(businesses);
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to load businesses",
      });
    },
  });

  const [updateStaff, { loading: salesLoading }] = useMutation(UPDATE_STAFF);

  const handleSubmit = (values) => {
    updateStaff({
      variables: {
        id: editRow.id,
        name: values.name,
        business_ids: values.business_ids.map(Number),
      },
      onCompleted: () => {
        showNotification({
          color: "green",
          title: "Success",
          message: "Staff member updated successfully",
        });
        setOpened(false);
      },
      onError: (error) => {
        console.error("Update staff error:", error);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to update staff",
        });
      },
    });
  };

  return (
    <>
      <LoadingOverlay
        visible={salesLoading || businessLoading}
        overlayBlur={2}
        loader={customLoader}
      />

      <ScrollArea type="never" style={{ maxHeight: "70vh", paddingRight: 12 }}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack spacing="lg">
            <Grid gutter="xl">
              <Grid.Col span={4}>
                <TextInput
                  required
                  label="Full Name"
                  placeholder="Enter full name"
                  {...form.getInputProps("name")}
                />
              </Grid.Col>

              <Grid.Col span={4}>
                <MultiSelect
                  required
                  label="Business"
                  placeholder="Select one or more businesses"
                  searchable
                  nothingFound="No businesses found"
                  maxDropdownHeight={280}
                  withinPortal={false}
                  data={businessDropDownData}
                  value={form.values.business_ids}
                  onChange={(values) =>
                    form.setFieldValue("business_ids", values)
                  }
                  error={form.errors.business_ids}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Button
                  style={{
                    width: "25%",
                    marginTop: "15px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  type="submit"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};
